import { Button, TextInput } from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import { useState } from 'react';

import { FeatureErrorBoundary } from '@/components/feature-error-boundary.tsx';
import { Footer } from '@/components/footer.tsx';
import { Page } from '@/components/page.tsx';
import { SparklesCore } from '@/components/sparkles-core.tsx';
import { Paragraph } from '@/components/text/paragraph.tsx';

import { DEFAULT_FOOTER_LINKS } from '@/constants/external-links.ts';

import { LoginStatus, useLogin } from '@/hooks/use-login.ts';

import { useUIStore } from '@/stores/ui-store.ts';

export const LoginPage = () => {
    const isFancyCheckinRendered = useUIStore((state) => state.isFancyCheckinRendered);
    const { status, showExternalForm, submitMagicLink, triggerGoogleSignIn } = useLogin();
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            email: '',
        },
        validateInputOnBlur: true,

        validate: {
            email: isEmail('Ungültige E-Mail Adresse'),
        },
    });

    const [submittedEmail, setSubmittedEmail] = useState('');

    const handleMagicLinkSignIn = async (): Promise<void> => {
        form.validate();
        if (!form.isValid('email')) return;
        const email = form.getValues().email;
        setSubmittedEmail(email);
        await submitMagicLink(email);
    };

    if (isFancyCheckinRendered) {
        return (
            <Page className="relative flex-row bg-black" fullScreen>
                <FeatureErrorBoundary featureName="sparkles-animation">
                    <div className="absolute inset-0 h-screen w-full">
                        <SparklesCore
                            background="transparent"
                            minSize={0.6}
                            maxSize={1.4}
                            particleDensity={5}
                            className="h-full w-full"
                            particleColor="#FFFFFF"
                        />
                    </div>
                </FeatureErrorBoundary>
                <div className="relative flex w-full flex-col items-center justify-center">
                    <div className="flex w-[40rem] flex-col items-center justify-center gap-5 rounded-md border border-white/10 bg-white/5 py-20 shadow-lg shadow-white/5 backdrop-blur-xs">
                        <h1 className="text-4xl text-white">CareDesk Login</h1>
                        <div className="flex min-h-[10rem] w-1/2 flex-col justify-center gap-5 text-center">
                            <TextInput
                                className="text-start"
                                placeholder="E-Mail Adresse"
                                {...form.getInputProps('email')}
                            />
                            <Button
                                disabled={!!form.errors.email || status === LoginStatus.SUBMITTING}
                                loading={status === LoginStatus.SUBMITTING}
                                onClick={handleMagicLinkSignIn}
                            >
                                Schicke mir den Login-Link per Email
                            </Button>
                            {status === LoginStatus.SUCCESS && (
                                <Paragraph className="text-white">{`Falls ein Account mit dieser Email-Adresse existiert, wurde Dir ein Anmeldelink an ${submittedEmail} zugeschickt`}</Paragraph>
                            )}
                            {status === LoginStatus.FAILURE && (
                                <Paragraph className="text-white">{`Erstellung des Anmeldelinks für ${submittedEmail} fehlgeschlagen`}</Paragraph>
                            )}
                            <div className="flex items-center gap-2 text-sm text-white">
                                <hr className="w-full border-t border-white/20" />
                                <span>oder</span>
                                <hr className="w-full border-t border-white/20" />
                            </div>
                            <Button
                                onClick={triggerGoogleSignIn}
                                variant="outline"
                                color="white"
                                className="text-white hover:bg-white/10 hover:text-white"
                                leftSection={
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                                        alt="Google Logo"
                                    />
                                }
                            >
                                Google SSO
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute inset-x-0 bottom-0">
                    <Footer links={DEFAULT_FOOTER_LINKS} />
                </div>
            </Page>
        );
    }

    return (
        <Page className="flex-row" fullScreen>
            <img
                className="h-screen w-[60%] object-cover"
                src="https://images.prismic.io/eh-patients/aMGACmGNHVfTO_f1_SpacesImage05.jpg?auto=format,compress"
                alt="Eterno Wartebereich"
            />
            <div className="flex flex-1 flex-col items-center justify-center gap-5">
                <h1 className="text-4xl">CareDesk Login</h1>
                <div className="flex min-h-[10rem] w-2/3 flex-col justify-center gap-5 text-center">
                    {status === LoginStatus.INITIAL && (
                        <>
                            <Button onClick={showExternalForm}>Kunden Login</Button>
                            <Button onClick={triggerGoogleSignIn} variant="outline">
                                Eterno Mitarbeiter Anmeldung
                            </Button>
                        </>
                    )}
                    {status !== LoginStatus.INITIAL && (
                        <>
                            <TextInput
                                className="text-start"
                                placeholder="E-Mail Adresse"
                                {...form.getInputProps('email')}
                            />
                            <Button
                                disabled={!!form.errors.email || status === LoginStatus.SUBMITTING}
                                loading={status === LoginStatus.SUBMITTING}
                                onClick={handleMagicLinkSignIn}
                            >
                                Schicke mir den Login-Link per Email
                            </Button>
                        </>
                    )}
                    {status === LoginStatus.SUCCESS && (
                        <Paragraph>{`Falls ein Account mit dieser Email-Adresse existiert, wurde Dir ein Anmeldelink an ${submittedEmail} zugeschickt`}</Paragraph>
                    )}
                    {status === LoginStatus.FAILURE && (
                        <Paragraph>{`Erstellung des Anmeldelinks für ${submittedEmail} fehlgeschlagen`}</Paragraph>
                    )}
                </div>
                <div className="fixed inset-x-0 bottom-0 w-full md:absolute md:inset-x-auto md:w-auto">
                    <Footer links={DEFAULT_FOOTER_LINKS} />
                </div>
            </div>
        </Page>
    );
};
